import React from 'react';
import { MapPin, Languages, Briefcase, Linkedin, Mail, Phone, Sparkles } from 'lucide-react';
import BioContent from './BioContent';

/**
 * Mini preview of an interpreter card — visually matches the public directory.
 * Used in the admin approval flow to show "before / after" side-by-side.
 * `highlightFields` (array of changed keys) highlights the sections that changed.
 */
export default function InterpreterPreviewCard({ data, label, accent = 'gray', highlightFields = [] }) {
  if (!data) return null;
  const changed = new Set(highlightFields || []);
  const langsChanged = changed.has('active_languages') || changed.has('passive_languages');
  const specialtyChanged = changed.has('specialty');
  const hl = 'ring-2 ring-amber-300 bg-amber-50/60 rounded-xl -mx-2 px-2 py-2';
  const ChangedTag = () => (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
      <Sparkles size={9} /> Modificado
    </span>
  );
  const initials = (data.full_name || '?')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  const accentMap = {
    gray: 'border-gray-200',
    cmic: 'border-cmic-400 ring-1 ring-cmic-200',
  };

  const specialties = (Array.isArray(data.specialty) ? data.specialty.join(',') : (data.specialty || ''))
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className={`bg-white rounded-2xl border ${accentMap[accent] || accentMap.gray} p-4 sm:p-5`}>
      {label && (
        <div className="text-[10px] uppercase tracking-wider font-semibold mb-3 text-charcoal-500">
          {label}
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        {data.photo_url ? (
          <img
            src={data.photo_url}
            alt={data.full_name}
            className="w-14 h-14 rounded-full object-cover border border-gray-200"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center font-bold">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-charcoal-800 truncate">{data.full_name}</div>
          {data.city && (
            <div className="text-xs text-charcoal-500 flex items-center gap-1 mt-0.5">
              <MapPin size={11} className="text-cmic-500" />
              {data.city}
            </div>
          )}
        </div>
      </div>

      {(data.active_languages?.length > 0 || data.passive_languages?.length > 0) && (
        <div className={`mb-3 ${langsChanged ? hl : ''}`}>
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-charcoal-500 flex items-center gap-1.5">
            <span className="flex items-center gap-1"><Languages size={10} /> Idiomas</span>
            {langsChanged && <ChangedTag />}
          </div>
          <div className="flex flex-wrap gap-1">
            {(Array.isArray(data.active_languages) ? data.active_languages : []).map((l) => (
              <span key={`a-${l}`} className="inline-flex items-center text-[10px] font-medium bg-cmic-100 text-cmic-700 px-1.5 py-0.5 rounded">{l}</span>
            ))}
            {(Array.isArray(data.passive_languages) ? data.passive_languages : []).map((l) => (
              <span key={`p-${l}`} className="inline-flex items-center text-[10px] font-medium bg-gray-100 text-charcoal-600 px-1.5 py-0.5 rounded border border-gray-200">{l}</span>
            ))}
          </div>
        </div>
      )}

      {specialties.length > 0 && (
        <div className={`mb-3 ${specialtyChanged ? hl : ''}`}>
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-charcoal-500 flex items-center gap-1.5">
            <span className="flex items-center gap-1"><Briefcase size={10} /> Especialidades</span>
            {specialtyChanged && <ChangedTag />}
          </div>
          <div className="flex flex-wrap gap-1">
            {specialties.map((s) => (
              <span key={s} className="inline-flex items-center text-[10px] font-medium bg-cmic-50 text-cmic-700 px-1.5 py-0.5 rounded border border-cmic-100">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1 mt-2 pt-2 border-t border-gray-100">
        {data.email && (
          <div className="text-[11px] text-charcoal-500 flex items-center gap-1.5 truncate">
            <Mail size={11} className="text-cmic-500" /> {data.email}
          </div>
        )}
        {data.phone && (
          <div className="text-[11px] text-charcoal-500 flex items-center gap-1.5">
            <Phone size={11} className="text-cmic-500" /> {data.phone}
          </div>
        )}
        {data.linkedin_url && (
          <div className="text-[11px] text-charcoal-500 flex items-center gap-1.5 truncate">
            <Linkedin size={11} className="text-cmic-500" /> LinkedIn
          </div>
        )}
      </div>

      {data.bio && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 text-charcoal-500">Semblanza</div>
          <div className="text-[11px] text-charcoal-600 line-clamp-4 leading-relaxed">
            <BioContent bio={data.bio} className="!text-[11px] prose-p:my-0.5" />
          </div>
        </div>
      )}
    </div>
  );
}
